import events from "add-event-listener";
import debounce from "lodash.debounce";
import * as React from "react";
import { getScrollWidth } from "react-ui";

import {
    SCROLLABLE_CONTAINER_LEFT_ARROW_CLICK,
    SCROLLABLE_CONTAINER_RIGHT_ARROW_CLICK,
    SCROLLABLE_CONTAINER_SCROLL_MOUSEDOWN,
} from "../../metricsAction";
import { IScrollToContext, scrollToContextTypes, ScrollToProvider } from "../../Internals/Contexts/ScrollToContext";
import { LayoutEvents, LayoutEventsSubscription } from "../../Internals/LayoutEvents/LayoutEvents";
import { trackAction } from "../../Internals/Metrics/MetricsProvider";
import { emptyRef, nullElement, pullContext } from "../../TypingUtils";
import Sticky from "../CrossfitTable/Internals/HackedControls/Sticky";
import { scrollLeft, scrollRight } from "../StickyTable/scroll";
import { ArrowLeft } from "../../Internals/Arrows/ArrowLeft";
import { ArrowRight } from "../../Internals/Arrows/ArrowRight";

import {
    Inner,
    LeftArrow,
    RightArrow,
    Root,
    Scroll,
    ScrollBar,
    ScrollBarContainer,
} from "./ScrollableContainer.styles";

const MIN_SCROLL_SIZE = 20;

interface ScrollableContainerProps {
    children?: React.ReactNode;
    fixed?: React.ReactElement<{ innerRef: (x: HTMLElement | null) => void }>;
    style?: React.CSSProperties;
    contentType?: "unit";
    getStop?: () => HTMLElement | null | undefined;
    refInner?: (el: ScrollableContainer) => void;
}

interface ScrollableContainerState {
    left: number;
}

type ScrollingSource = "none" | "container" | "inner" | "scrollbar";

export class ScrollableContainer extends React.Component<ScrollableContainerProps, ScrollableContainerState> {
    public static contextTypes = {
        ...scrollToContextTypes,
    };

    public state: ScrollableContainerState = { left: 0 };

    private scrollingSource: ScrollingSource = "none";
    private readonly container = React.createRef<HTMLDivElement>();
    private fixed: HTMLElement | null = emptyRef;
    private readonly inner = React.createRef<HTMLDivElement>();
    private readonly leftArrow = React.createRef<HTMLDivElement>();
    private readonly rightArrow = React.createRef<HTMLDivElement>();
    private readonly scroll = React.createRef<HTMLDivElement>();
    private readonly scrollbar = React.createRef<HTMLDivElement>();
    private readonly stop = React.createRef<HTMLDivElement>();

    private readonly setScrollingSource = debounce((pos: ScrollingSource): void => {
        this.scrollingSource = pos;
    }, 50);
    private layoutEventsSubscription?: LayoutEventsSubscription;

    public componentDidMount(): void {
        if (this.props.refInner) {
            this.props.refInner(this);
        }

        if (this.container.current != undefined) {
            const { left } = this.container.current.getBoundingClientRect();
            this.setState({ left: left });
        }

        this.adjustPositions();
        this.layoutEventsSubscription = LayoutEvents.addListener(this.adjustPositions);
        if (this.scrollbar.current != undefined) {
            events.addEventListener(this.scrollbar.current, "scroll", this.syncContainerScrollLeft);
        }
        if (this.container.current != undefined) {
            events.addEventListener(this.container.current, "scroll", this.syncScrollbarScrollLeft);
        }
        if (this.inner.current != undefined) {
            events.addEventListener(this.inner.current, "scroll", this.syncInnerScrollLeft);
        }
    }

    public UNSAFE_componentWillReceiveProps(): void {
        setTimeout(() => {
            if (this.container.current != undefined) {
                const { left } = this.container.current.getBoundingClientRect();
                this.setState({ left: left });
            }
        }, 300);
    }

    public componentDidUpdate(): void {
        LayoutEvents.emit();
    }

    public componentWillUnmount(): void {
        if (this.layoutEventsSubscription != undefined) {
            this.layoutEventsSubscription.remove();
        }
        if (this.scrollbar.current != undefined) {
            events.removeEventListener(this.scrollbar.current, "scroll", this.syncContainerScrollLeft);
        }
        if (this.container.current != undefined) {
            events.removeEventListener(this.container.current, "scroll", this.syncScrollbarScrollLeft);
        }
        if (this.inner.current != undefined) {
            events.removeEventListener(this.inner.current, "scroll", this.syncInnerScrollLeft);
        }
    }

    public render(): JSX.Element {
        const { children, contentType, style } = this.props;

        return (
            <ScrollToProvider value={this.scrollTo}>
                <Root ref={this.container} style={style}>
                    <div ref={this.stop} />
                    <LeftArrow
                        ref={this.leftArrow}
                        data-tid="ScrollableLeftArrow"
                        onClick={this.handleOnClickLeftArrow}>
                        <ArrowLeft />
                    </LeftArrow>
                    <RightArrow
                        ref={this.rightArrow}
                        data-tid="ScrollableRightArrow"
                        onClick={this.handleOnClickRightArrow}>
                        <ArrowRight />
                    </RightArrow>
                    {this.renderFixed()}
                    <Inner unit={contentType === "unit"} ref={this.inner}>
                        {children}
                    </Inner>
                    <Sticky
                        bottomAddition={50}
                        getStop={this.getStop}
                        offset={20}
                        side="bottom"
                        contentType={contentType}>
                        {(fixed: boolean): JSX.Element => (
                            <ScrollBarContainer
                                fixed={fixed}
                                unit={contentType === "unit"}
                                style={fixed ? { left: this.state.left } : {}}>
                                <div>
                                    <ScrollBar ref={this.scrollbar}>
                                        <Scroll ref={this.scroll} onMouseDown={this.handleScrollMouseDown} />
                                    </ScrollBar>
                                </div>
                            </ScrollBarContainer>
                        )}
                    </Sticky>
                </Root>
            </ScrollToProvider>
        );
    }

    private renderFixed(): JSX.Element | null {
        return this.props.fixed
            ? React.cloneElement(this.props.fixed, { innerRef: (x: null | HTMLElement) => (this.fixed = x) })
            : nullElement;
    }

    private readonly adjustFixedPositions = () => {
        const [shadowType, shadowValue] = ["box-shadow", "3px 0px 5px 0px rgba(54,41,2,0.2)"];
        if (this.fixed == undefined || this.inner.current == undefined) {
            return;
        }
        // @ts-ignore
        this.fixed.style[shadowType] = this.inner.current.scrollLeft ? shadowValue : "none";
        this.fixed.style.height = `${this.inner.current.scrollHeight - parseInt(this.fixed.style.top || "0", 10)}px`;
        this.inner.current.style.paddingLeft = `${this.fixed.offsetWidth}px`;
    };

    private readonly adjustPositions = () => {
        if (this.inner.current === null || this.scrollbar.current === null) {
            return;
        }

        if (this.fixed) {
            this.adjustFixedPositions();
        }

        const containerWidth = this.inner.current.offsetWidth;
        const contentWidth = this.inner.current.scrollWidth;

        // When screen size !== 100%
        if (containerWidth + 5 >= contentWidth) {
            if (this.scrollbar.current.style.display !== "none") {
                this.scrollbar.current.style.display = "none";
            }
            if (this.rightArrow.current != undefined) {
                if (this.rightArrow.current.style.display !== "none") {
                    this.rightArrow.current.style.display = "none";
                }
            }
            if (this.leftArrow.current != undefined) {
                if (this.leftArrow.current.style.display !== "none") {
                    this.leftArrow.current.style.display = "none";
                }
            }

            return;
        }

        this.setScrollStyle(containerWidth, contentWidth);
        this.setArrowsStyle(containerWidth, contentWidth);
    };

    private setScrollStyle(containerWidth: number, contentWidth: number): void {
        const container = this.container.current;
        const inner = this.inner.current;
        const scrollbar = this.scrollbar.current;
        const scroll = this.scroll.current;

        if (scrollbar == undefined || container == undefined || inner == undefined || scroll == undefined) {
            return;
        }
        scrollbar.style.display = "block";
        scrollbar.style.left = `${container.getBoundingClientRect().left}px`;
        scrollbar.style.width = `${container.getBoundingClientRect().right - container.getBoundingClientRect().left}px`;
        scrollbar.scrollLeft = container.scrollLeft;

        // NOTE 80 - container paddings
        const scrollSize = Math.max(MIN_SCROLL_SIZE, (containerWidth * containerWidth) / (contentWidth + 80));
        const scrollPos = (inner.scrollLeft * (container.offsetWidth - scrollSize)) / (contentWidth - containerWidth);

        scroll.style.width = `${scrollSize}px`;
        scroll.style.left = `${scrollPos}px`;

        inner.style.marginBottom = `${-getScrollWidth()}px`;
    }

    private setArrowsStyle(containerWidth: number, contentWidth: number): void {
        const inner = this.inner.current;
        if (inner == undefined) {
            return;
        }
        const innerRect = inner.getBoundingClientRect();
        const screenHeight = document.documentElement.offsetHeight;
        const isInnerRectBottom = screenHeight / (innerRect.bottom - 80) > 2 || innerRect.bottom < 130;
        const isInnerRectTop = screenHeight / (innerRect.top + 130) < 2 && innerRect.top > 0;

        let top = "80px";
        let bottom = "auto";

        if (isInnerRectBottom) {
            [top, bottom] = [bottom, top];
        }
        const style =
            isInnerRectBottom || isInnerRectTop
                ? {
                      bottom: bottom,
                      top: top,
                      left: "0",
                      position: "absolute",
                      right: "0",
                  }
                : {
                      bottom: "50%",
                      left: `${this.state.left}px`,
                      position: "fixed",
                      right: "40px",
                      top: "auto",
                  };

        const commonKeys = ["bottom", "position", "top"];

        if (this.leftArrow.current != undefined) {
            if (inner.scrollLeft) {
                this.leftArrow.current.style.display = "block";
                for (const key of [...commonKeys, "left"]) {
                    // @ts-ignore
                    this.leftArrow.current.style[key] = style[key];
                }
            } else {
                if (this.leftArrow.current.style.display !== "none") {
                    this.leftArrow.current.style.display = "none";
                }
            }
        }

        if (this.rightArrow.current != undefined) {
            if (inner.scrollLeft + containerWidth < contentWidth) {
                this.rightArrow.current.style.display = "block";
                for (const key of [...commonKeys, "right"]) {
                    // @ts-ignore
                    this.rightArrow.current.style[key] = style[key];
                }
            } else {
                if (this.rightArrow.current.style.display !== "none") {
                    this.rightArrow.current.style.display = "none";
                }
            }
        }
    }

    private readonly syncContainerScrollLeft = () => {
        if (this.scrollingSource === "scrollbar" || this.scrollingSource === "none") {
            this.scrollingSource = "scrollbar";
            if (this.container.current != undefined && this.scrollbar.current != undefined) {
                this.container.current.scrollLeft = this.scrollbar.current.scrollLeft;
            }
            this.setScrollingSource("none");
        }
        LayoutEvents.emit();
    };

    private readonly syncInnerScrollLeft = () => {
        if (
            this.scrollingSource === "scrollbar" ||
            this.scrollingSource === "none" ||
            this.scrollingSource === "container"
        ) {
            this.scrollingSource = "scrollbar";
            if (this.container.current != undefined && this.scrollbar.current != undefined) {
                this.container.current.scrollLeft = this.scrollbar.current.scrollLeft;
            }
            this.adjustPositions();
            this.setScrollingSource("none");
        }
        LayoutEvents.emit();
    };

    private readonly syncScrollbarScrollLeft = () => {
        if (this.scrollingSource === "container" || this.scrollingSource === "none") {
            this.scrollingSource = "container";
            if (this.container.current != undefined && this.scrollbar.current != undefined) {
                this.scrollbar.current.scrollLeft = this.container.current.scrollLeft;
            }

            this.setScrollingSource("none");
        }
        LayoutEvents.emit();
    };

    @trackAction(SCROLLABLE_CONTAINER_SCROLL_MOUSEDOWN)
    private readonly handleScrollMouseDown = (scrollMouseDownEvent: React.MouseEvent) => {
        if (this.inner.current === null) {
            return;
        }
        // @ts-ignore
        const target: Document = global.document;
        const initialX = scrollMouseDownEvent.clientX;
        const initialScrollLeft = this.inner.current.scrollLeft;

        const mouseMove = (mouseMoveEvent: MouseEvent) => {
            if (this.inner.current == undefined || this.scroll.current == undefined) {
                return;
            }
            const width = window.getComputedStyle(this.scroll.current).width;
            if (width == undefined) {
                return;
            }
            const ratio =
                (this.inner.current.scrollWidth - this.inner.current.offsetWidth) /
                (this.inner.current.offsetWidth - parseFloat(width));
            const deltaX = (mouseMoveEvent.clientX - initialX) * ratio;

            this.inner.current.scrollLeft = initialScrollLeft + deltaX;

            if (mouseMoveEvent.preventDefault) {
                mouseMoveEvent.preventDefault();
            } else {
                mouseMoveEvent.returnValue = false;
            }
        };
        const mouseUp = () => {
            events.removeEventListener(target, "mousemove", mouseMove);
            events.removeEventListener(target, "mouseup", mouseUp);
        };

        events.addEventListener(target, "mousemove", mouseMove);
        events.addEventListener(target, "mouseup", mouseUp);

        scrollMouseDownEvent.preventDefault();
    };

    private readonly getStop = (): undefined | null | HTMLElement => {
        if (this.props.getStop) {
            return this.props.getStop();
        }

        return this.stop.current;
    };

    @trackAction(SCROLLABLE_CONTAINER_LEFT_ARROW_CLICK)
    private readonly handleOnClickLeftArrow = () => {
        if (this.inner.current == undefined) {
            return;
        }
        const offset = this.inner.current.clientWidth / 1.5;

        scrollLeft(this.inner.current, offset);
    };

    @trackAction(SCROLLABLE_CONTAINER_RIGHT_ARROW_CLICK)
    private readonly handleOnClickRightArrow = () => {
        if (this.inner.current == undefined) {
            return;
        }
        const offset = this.inner.current.clientWidth / 1.5;

        scrollRight(this.inner.current, offset);
    };

    private readonly scrollTo = (component: Element) => {
        if (this.inner.current === null) {
            return;
        }
        this.getContext().scrollTo?.(component);
        const { left, right } = component.getBoundingClientRect();
        const { clientWidth } = document.documentElement;
        const { offsetWidth } = this.inner.current;

        // 50 - отступ, 80 - ширина сайдбара
        if (right > clientWidth) {
            this.inner.current.scrollLeft += right - clientWidth + 50;
        } else if (clientWidth - offsetWidth > left - (this.props.contentType === "unit" ? 80 : 0)) {
            this.inner.current.scrollLeft -= clientWidth - offsetWidth - left + 80;
        }
    };

    public getContext(): IScrollToContext {
        return pullContext<IScrollToContext>(this);
    }
}
